import { useAppSelector } from 'hooks/redux';
import React from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { businessSelectors, businessTypes } from 'app-components/flux/business';
import type { BusinessTaxInfoType } from 'app-components/flux/categories/categoriesTypes';
import { useManager, useMultiFields } from 'app-components/flux/categories/categoryFormHooks';

import TaxFormBusiness from './TaxFormBusiness';
import TaxLineItemBusiness from './TaxLineItemBusiness';
import TaxNavButton from './TaxNavButton';

import { taxGroupCategoryNames } from 'app-components/flux/business/businessTypes';
import useStyles from './styles';
import { initialCaps } from './utils';

type FormToggleProps = {
  type: string;
};

const FormToggle = ({ type }: FormToggleProps) => {
  const { value, onValueChange } = useMultiFields('businessTaxInfo', type);
  const { enabled } = value as BusinessTaxInfoType;

  return (
    <Switch
      checked={enabled}
      onChange={(e, value) => {
        e.stopPropagation();
        onValueChange({ name: 'enabled', value });
      }}
    />
  );
};

const BusinessTaxInfo = ({ type }: FormToggleProps) => {
  const { value } = useMultiFields('businessTaxInfo', type);

  return (
    <>
      {value.enabled && (
        <Stack direction='column' spacing={3}>
          <TaxFormBusiness type={type} />
          <TaxLineItemBusiness type={type} />
        </Stack>
      )}
    </>
  );
};

const TaxInfoQBPBusiness = ({ onSelectTaxGroup }: { onSelectTaxGroup: () => void }) => {
  const { classes } = useStyles();

  const manager = useManager();

  // get the taxGroups corresponding to the business that the user has setup
  const businesses = useAppSelector(businessSelectors.getBusinessList);
  const activeTaxGroups = Array.from(new Set(businesses.map((obj) => obj.taxGroup)));

  return (
    <Stack mt={2} spacing={3} direction='column' flex='1'>
      {Object.entries(businessTypes.taxGroupNames).map(
        ([key, value]) =>
          (activeTaxGroups.includes(key) || manager.getMultiFieldValue('businessTaxInfo', key).enabled) && (
            <Card key={key} className={classes.taxGroupCard} data-testid={`tax-group-card-${key}`}>
              <Stack direction='column' flex='1' spacing={3}>
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                  <Typography variant='bodyMedium'>
                    {key in taxGroupCategoryNames
                      ? taxGroupCategoryNames[key as keyof typeof taxGroupCategoryNames]
                      : initialCaps(key)}{' '}
                    (i.e. {value})
                  </Typography>
                  <FormToggle type={key} />
                </Stack>
                <BusinessTaxInfo type={key} />
              </Stack>
            </Card>
          ),
      )}

      <TaxNavButton id='selectTaxGroups' text='Select tax groups' onClick={onSelectTaxGroup} />
    </Stack>
  );
};

export default TaxInfoQBPBusiness;
